import type { MessageResponse, PagingModeEnum, RingilApiType, TableViewOutDTO, TableViewPrefsOutDTO, ViewVisibilityEnum } from '@api/ringil-api.v2';

// The generated client is not imported at runtime here (tests run it without React), so the enum's one value is spelled out.
const SCROLL = 'SCROLL' as PagingModeEnum;
import type { Callbacks } from '@api/apiRuntime';
import type { TableView, TableViewPrefs } from './types';
import { cleanColumnWidths, TableViewStore, TableViewStoreError } from './viewStore';

export type TableViewsApi = Pick<
  RingilApiType,
  'getTableViews' | 'createTableView' | 'updateTableView' | 'setTableViewVisibility' | 'deleteTableView' | 'saveTableViewPrefs'
>;

export interface ApiViewStoreNotify {
  /** `200 VALIDATION` — e.g. the limit of own views (`TableViews.tooMany`, on `name`). */
  validation: (messages: MessageResponse[]) => void;
  /** `409 CONFLICT` — the view was saved elsewhere since it was read. */
  conflict: () => void;
}

/**
 * The generated type says `filter: string`, but the backend sends and takes the JSON object (a
 * `JsonNode`, stored verbatim). A string is still accepted, in case the generator's view of it wins.
 */
const filterFromDTO = (filter: unknown): Record<string, unknown> => {
  if (typeof filter === 'string') {
    try {
      return filterFromDTO(JSON.parse(filter));
    } catch {
      return {};
    }
  }
  return filter && typeof filter === 'object' && !Array.isArray(filter) ? (filter as Record<string, unknown>) : {};
};

/** What the API returns, in the table's model. Further reconciliation (unknown fields, a bad sort) is sanitizeView's job. */
export const viewFromDTO = (dto: TableViewOutDTO): TableView => ({
  id: dto.id,
  tableKey: dto.tableKey,
  name: dto.name,
  ownerId: dto.ownerId,
  ownerFullName: dto.ownerFullName,
  editable: dto.editable,
  visibility: dto.viewVisibility === 'COMPANY' ? 'COMPANY' : 'PRIVATE',
  createdAt: dto.createdAt,
  updatedAt: dto.updatedAt,
  version: dto.version,
  filter: filterFromDTO(dto.filter),
  // null for a view saved before it had a page size: the table's default.
  ...(dto.pageSize ? { pageSize: dto.pageSize } : {}),
  // SCROLL is the only value; absent = pages.
  ...(dto.pagingMode === SCROLL ? { pagingMode: 'scroll' as const } : {}),
  sort: { sortField: dto.sort?.sortField, sortDirection: dto.sort?.sortDirection === 'ASC' ? 'ASC' : 'DESC' },
  columns: (dto.columns ?? []).map(column => ({
    id: column.id,
    members: column.members ?? [],
    ...(column.title ? { title: column.title } : {}),
    ...(column.width ? { width: column.width } : {}),
  })),
  // null = the table's defaults, [] = only the active filters; the two must not merge.
  ...(Array.isArray(dto.favouriteFilters) ? { favouriteFilters: dto.favouriteFilters } : {}),
});

export const prefsFromDTO = (dto: TableViewPrefsOutDTO, tableKey: string): TableViewPrefs => ({
  tableKey,
  order: dto?.order ?? [],
  hidden: dto?.hidden ?? [],
  ...(dto?.activeViewId ? { activeViewId: dto.activeViewId } : {}),
  // Generated as `any`: checked here rather than trusted.
  ...(pagingModesOf(dto) ? { pagingModes: pagingModesOf(dto) } : {}),
  ...(dto?.columnWidths ? { columnWidths: cleanColumnWidths(dto.columnWidths) } : {}),
});

const pagingModesOf = (dto: TableViewPrefsOutDTO): Record<string, 'pages' | 'scroll'> | undefined => {
  const raw: unknown = dto?.pagingModes;
  if (!raw || typeof raw !== 'object') return undefined;
  return Object.fromEntries(
    Object.entries(raw)
      .filter(([, mode]) => mode === 'SCROLL' || mode === 'PAGES')
      .map(([id, mode]) => [id, mode === 'SCROLL' ? 'scroll' : 'pages']),
  );
};

/** Name and content as both create and update take them. `favouriteFilters` goes as null, not left out, when it is the defaults. */
const contentToDTO = (view: TableView) => ({
  name: view.name,
  filter: view.filter as never,
  sort: { sortField: view.sort.sortField, sortDirection: view.sort.sortDirection },
  columns: view.columns.map(column => ({
    id: column.id,
    members: column.members,
    ...(column.title ? { title: column.title } : {}),
    ...(column.width ? { width: column.width } : {}),
  })),
  favouriteFilters: view.favouriteFilters ?? null,
  // Update is a full replace, so the size goes on every save — leaving it out would reset it.
  pageSize: view.pageSize ?? null,
  // null = pages, the default; full replace as above.
  pagingMode: view.pagingMode === 'scroll' ? SCROLL : null,
});

/**
 * Where tabs are kept. The backend for RD-2957 is in `frontend-service`; until it is deployed on an
 * environment the endpoints answer 404, so flip this to `false` to run on localStorage there.
 */
export const TABLE_VIEWS_ON_BACKEND = true;

const isHttpConflict = (error: unknown): boolean => (error as { response?: { status?: number } })?.response?.status === 409;

/**
 * Views and prefs on the backend (`/api/v1.0/table-views/{tableKey}`). Ids and versions come from the
 * server: a created view gets its id there, and each save sends the version last read and returns the
 * next one.
 *
 * Every failure is reported before the promise rejects, so callers only keep their state: the
 * standard error toast for errors, `notify.validation` for a `200 VALIDATION`, `notify.conflict`
 * for a stale version (`409`). A request cancelled by navigation never settles, as it is dropped.
 *
 * `getApi` is read on every call, so the store can be created once while the API context (runAs) changes.
 */
export const createApiViewStore = (getApi: () => TableViewsApi, notify: ApiViewStoreNotify): TableViewStore => {
  const call = <T>(run: (resolve: (value: T) => void, callbacks: Callbacks) => void, extra: Callbacks = {}): Promise<T> =>
    new Promise<T>((resolve, reject) =>
      run(resolve, {
        ...extra,
        onValidationFailed: messages => {
          notify.validation(messages ?? []);
          reject(new TableViewStoreError('failed'));
        },
        onConflict: () => {
          notify.conflict();
          reject(new TableViewStoreError('conflict'));
        },
        onError: (error, showStandardError) => {
          if (isHttpConflict(error)) {
            notify.conflict();
            reject(new TableViewStoreError('conflict'));
            return;
          }
          showStandardError();
          reject(new TableViewStoreError('failed'));
        },
      }),
    );

  return {
    load: tableKey =>
      call((resolve, callbacks) =>
        getApi().getTableViews(tableKey, data => resolve({ views: (data?.views ?? []).map(viewFromDTO), prefs: prefsFromDTO(data?.prefs, tableKey) }), null, callbacks),
      ),
    createView: (view, options) =>
      call<TableView>(
        (resolve, callbacks) => getApi().createTableView(view.tableKey, contentToDTO(view), data => resolve(viewFromDTO(data)), null, callbacks),
        options?.silent ? { successToast: false } : {},
      ),
    updateView: view =>
      call<TableView>((resolve, callbacks) =>
        getApi().updateTableView(view.tableKey, view.id, { ...contentToDTO(view), version: view.version }, data => resolve(viewFromDTO(data)), null, callbacks),
      ),
    setVisibility: (view, visibility) =>
      call<TableView>((resolve, callbacks) =>
        getApi().setTableViewVisibility(
          view.tableKey,
          view.id,
          { viewVisibility: visibility as ViewVisibilityEnum, version: view.version },
          data => resolve(viewFromDTO(data)),
          null,
          callbacks,
        ),
      ),
    deleteView: (tableKey, viewId) => call<void>((resolve, callbacks) => getApi().deleteTableView(tableKey, viewId, () => resolve(), null, callbacks)),
    savePrefs: prefs =>
      call<TableViewPrefs>((resolve, callbacks) =>
        getApi().saveTableViewPrefs(
          prefs.tableKey,
          {
            order: prefs.order,
            hidden: prefs.hidden,
            ...(prefs.activeViewId ? { activeViewId: prefs.activeViewId } : {}),
            // Full replace like the rest of the prefs: every override goes every time.
            pagingModes: Object.fromEntries(Object.entries(prefs.pagingModes ?? {}).map(([id, mode]) => [id, mode === 'scroll' ? 'SCROLL' : 'PAGES'])),
            columnWidths: prefs.columnWidths ?? {},
          },
          data => resolve(prefsFromDTO(data, prefs.tableKey)),
          null,
          callbacks,
        ),
      ),
  };
};
